import { MAX_REPORT_BYTES, TooLarge } from '../domain/limits'
import { normaliseReport } from '../domain/normalise'
import type { Report } from '../domain/types'
import { allMeta, allReports, type StoredReport } from './db'
import { downloadBlob } from './download'
import type { Project } from '../domain/projects'
import type { Filters } from './viewState'

/**
 * Taking a session out of the browser, and bringing one back in.
 *
 * The file embeds the original assessment documents rather than pointing at
 * them. A shared session has to open correctly for the person receiving it,
 * which means it cannot depend on a URL that may be private, may have moved, or
 * may simply be gone by the time they open it.
 *
 * Where a report came from is kept alongside, so it can still be refreshed from
 * its source later. That is a convenience, not a dependency.
 */

export const SESSION_FORMAT = 'everse-quality-session'

/**
 * 2 adds the view block. Version 1 carried the reports, the filters and the
 * projects, so opening one gave back the data without the state it was read in:
 * the filters were written but never applied, and the page you were on, the
 * sources you follow and the per-page selections were not written at all.
 */
export const SESSION_VERSION = 2

export interface SessionFile {
  format: typeof SESSION_FORMAT
  version: number
  exportedAt: string
  reports: StoredReport[]
  /**
   * Everything the interface remembers, exactly as stored: filters, projects,
   * followed sources, the last page, and each page's own selections.
   *
   * Kept opaque on purpose. It is copied out of storage and back into it
   * without being enumerated here, so a preference added later travels in an
   * exported session without this file having to learn its name.
   */
  view?: Record<string, unknown>
  /** Chart colours, which change what the charts actually look like. */
  palette?: string
  /** Version 1 wrote these at the top level. Read, never written. */
  filters?: Filters
  projects?: Project[]
}

export class InvalidSessionFile extends Error {}

/**
 * Real sessions hold tens or hundreds. Each report is read and stored on the
 * page's own thread, so a file of a hundred thousand tiny ones would freeze it.
 */
export const MAX_SESSION_REPORTS = 2_000

export function buildSessionFile(
  reports: StoredReport[],
  view?: Record<string, unknown>,
  palette?: string,
): SessionFile {
  return {
    format: SESSION_FORMAT,
    version: SESSION_VERSION,
    exportedAt: new Date().toISOString(),
    reports,
    view,
    palette,
  }
}

/**
 * Checked rather than trusted. A session file is something a colleague sends
 * you, so it gets the same scepticism as any other external input.
 */
export function parseSessionFile(input: unknown): SessionFile {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    throw new InvalidSessionFile('This file does not contain a session.')
  }

  const candidate = input as Partial<SessionFile>

  if (candidate.format !== SESSION_FORMAT) {
    throw new InvalidSessionFile(
      'This is not a session file. It may be a single assessment report, which can be opened with "Add files".',
    )
  }

  if (typeof candidate.version !== 'number') {
    throw new InvalidSessionFile('This session file does not say which version it is.')
  }

  if (candidate.version > SESSION_VERSION) {
    throw new InvalidSessionFile(
      `This session was written by a newer version of the application (format ${candidate.version}, this one reads ${SESSION_VERSION}).`,
    )
  }

  if (!Array.isArray(candidate.reports)) {
    throw new InvalidSessionFile('This session file contains no reports.')
  }

  if (candidate.reports.length > MAX_SESSION_REPORTS) {
    throw new InvalidSessionFile(
      `This session file holds ${candidate.reports.length} reports; this reads at most ${MAX_SESSION_REPORTS}.`,
    )
  }

  const reports: StoredReport[] = []
  for (const entry of candidate.reports) {
    if (
      typeof entry !== 'object' ||
      entry === null ||
      typeof (entry as StoredReport).id !== 'string' ||
      (entry as StoredReport).raw === undefined
    ) {
      // One malformed entry should not cost the reader the whole session.
      continue
    }
    const stored = entry as StoredReport
    reports.push({
      id: stored.id,
      raw: stored.raw,
      // Its fields are shown and acted on (sync, removal), so a source that is
      // not exactly the expected shape is replaced, not trusted.
      source: isReportSource(stored.source)
        ? stored.source
        : { kind: 'file', ref: 'imported', loadedAt: new Date().toISOString() },
      addedAt: typeof stored.addedAt === 'number' ? stored.addedAt : Date.now(),
    })
  }

  if (reports.length === 0) {
    throw new InvalidSessionFile('None of the reports in this session file could be read.')
  }

  return {
    format: SESSION_FORMAT,
    version: candidate.version,
    exportedAt: typeof candidate.exportedAt === 'string' ? candidate.exportedAt : '',
    reports,
    view: readView(candidate),
    palette: typeof candidate.palette === 'string' ? candidate.palette : undefined,
  }
}

/**
 * The view state, wherever this file's version happened to put it.
 *
 * Version 1 wrote filters and projects as top level fields under the names they
 * are stored by, so lifting them into the view block is the whole migration.
 */
function readView(candidate: Partial<SessionFile>): Record<string, unknown> | undefined {
  // JSON.parse keeps a key named __proto__ as an ordinary entry; stored and
  // read back into an object, it would set that object's prototype instead.
  const view: Record<string, unknown> =
    typeof candidate.view === 'object' && candidate.view !== null && !Array.isArray(candidate.view)
      ? Object.fromEntries(Object.entries(candidate.view).filter(([key]) => key !== '__proto__'))
      : {}

  if (view.filters === undefined && candidate.filters !== undefined) {
    view.filters = candidate.filters
  }
  if (view.projects === undefined && Array.isArray(candidate.projects)) {
    view.projects = candidate.projects
  }

  return Object.keys(view).length > 0 ? view : undefined
}

/**
 * The session in this browser, written to a file. Read from storage rather
 * than from state: what gets written out is the original documents, which
 * state does not hold. The view state comes from the same place and is taken
 * wholesale, so a preference added later travels with the session unchanged.
 */
export async function exportSessionFile(palette?: string): Promise<void> {
  downloadBlob(await sessionFileBlob(palette), sessionFilename())
}

async function sessionFileBlob(palette?: string): Promise<Blob> {
  const file = buildSessionFile(await allReports(), await allMeta(), palette)
  return new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' })
}

/**
 * The reports in a session file, read again, each paired with its own entry.
 *
 * Pairing by position afterwards put every report after an unreadable one
 * against the wrong stored document. The stored id is the report's content
 * hash, not whatever the file claims, so removing it later finds it.
 */
export function readSessionReports(file: SessionFile): {
  restored: { report: Report; stored: StoredReport }[]
  failed: { ref: string; reason: string }[]
} {
  const restored: { report: Report; stored: StoredReport }[] = []
  const failed: { ref: string; reason: string }[] = []
  for (const entry of file.reports) {
    try {
      // The limit a single report file has, so a session cannot carry past it.
      if (JSON.stringify(entry.raw).length > MAX_REPORT_BYTES) {
        failed.push({
          ref: entry.source?.ref ?? entry.id,
          reason: new TooLarge('This report', MAX_REPORT_BYTES).message,
        })
        continue
      }
      const report = normaliseReport(entry.raw, entry.source)
      restored.push({ report, stored: { ...entry, id: report.id } })
    } catch {
      failed.push({ ref: entry.source?.ref ?? entry.id, reason: 'No longer readable.' })
    }
  }
  return { restored, failed }
}

/** A filename that sorts sensibly and says what it is. */
export function sessionFilename(now = new Date()): string {
  return `everse-session-${now.toISOString().slice(0, 10)}.json`
}

const SOURCE_KINDS: readonly string[] = ['file', 'url', 'catalog', 'bundled']

function isReportSource(value: unknown): value is StoredReport['source'] {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    SOURCE_KINDS.includes(v.kind as string) &&
    typeof v.ref === 'string' &&
    typeof v.loadedAt === 'string'
  )
}
